// --- Dependencies -------------------------------------------------------------------------------
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
// --- Our Modules --------------------------------------------------------------------------------
import usePopup from "@/hooks/usePopup";
import type { TyAnchorRect, TyPopupAnim, TyPopupComp } from "@/hooks/usePopup/types";
import "./styles.css";
import "./side-form.css";
// --- Constants ----------------------------------------------------------------------------------
const ANIM_DURATION = 200;
const ANCHOR_GAP = 6;
/** Breathing room between an anchored popup and the edge of the window. */
const EDGE_PAD = 8;
// --- Helpers ------------------------------------------------------------------------------------
/** The horizontal half: hang off the opener, flipping to the far edge past the middle of the screen. */
function anchorSide(anchor: TyAnchorRect): CSSProperties {
    const isPastMiddle = (anchor.left + anchor.right) / 2 >= window.innerWidth / 2;
    return {
        position: "fixed",
        minWidth: anchor.width,
        ...(isPastMiddle ? { right: window.innerWidth - anchor.right } : { left: anchor.left }),
    };
}

/**
 * The vertical half, and it has to be measured rather than guessed.
 *
 * A menu opened from a button near the foot of the screen used to hang below it and off the bottom —
 * unreachable, because the page behind a popup is frozen and cannot be scrolled to it. So the popup is
 * laid out, its height read, and if it will not fit it is pinned **above** its opener instead, with
 * the animation flipped to grow from the right edge.
 */
function useAnchorStyle(
    anchor: TyAnchorRect | undefined,
    fallbackAnim: TyPopupAnim
): { style: CSSProperties; anim: TyPopupAnim; ref: React.RefObject<HTMLDivElement | null> } {
    const ref = useRef<HTMLDivElement>(null);
    const [style, setStyle] = useState<CSSProperties>({ position: "fixed", visibility: "hidden" });
    const [anim, setAnim] = useState<TyPopupAnim>(fallbackAnim);

    useLayoutEffect(() => {
        if (!anchor) return;
        const base = anchorSide(anchor);
        const height = ref.current?.offsetHeight ?? 0;

        if (anchor.bottom + ANCHOR_GAP + height > window.innerHeight - EDGE_PAD) {
            setStyle({ ...base, bottom: window.innerHeight - anchor.top + ANCHOR_GAP, maxHeight: anchor.top - ANCHOR_GAP - EDGE_PAD });
            setAnim("scaleBottom");
        } else {
            setStyle({ ...base, top: anchor.bottom + ANCHOR_GAP, maxHeight: window.innerHeight - anchor.bottom - ANCHOR_GAP - EDGE_PAD });
            setAnim(fallbackAnim);
        }
    }, [anchor, fallbackAnim]);

    return { style, anim, ref };
}
// --- Logic --------------------------------------------------------------------------------------
type PopupProps = { comp: TyPopupComp; index: number };

function Popup({ comp, index }: PopupProps) {
    const closing = usePopup((state) => state.closing.includes(comp.name));
    const removeComp = usePopup((state) => state.removeComp);
    const requestClose = usePopup((state) => state.requestClose);
    const [isOpen, setIsOpen] = useState(false);

    // One frame closed, then open — without it the element mounts already in its final position.
    useEffect(() => {
        const frame = requestAnimationFrame(() => setIsOpen(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    // `closing` already drives the exit state below; this only clears the element once it has played.
    useEffect(() => {
        if (!closing) return;
        const timer = setTimeout(() => removeComp(comp.name), ANIM_DURATION);
        return () => clearTimeout(timer);
    }, [closing, comp.name, removeComp]);

    /**
     * Stable across renders on purpose. A popup that puts `onClose` in an effect's deps would see a new
     * function every render, re-run the effect, close and reopen itself — a loop that hangs the tab.
     */
    const onClose = useCallback(() => requestClose(comp.name), [requestClose, comp.name]);
    const state = isOpen && !closing ? "open" : "closed";
    const isAnchored = comp.place === "anchor-below" && !!comp.anchor;
    const anchored = useAnchorStyle(isAnchored ? comp.anchor : undefined, comp.anim ?? "scaleTop");

    return (
        <div
            className={`popup popup--${comp.place}`}
            style={{ zIndex: 50 + index }}
            data-cover={comp.cover}
            data-state={state}
            onClick={comp.coverClose === false ? undefined : onClose}
        >
            <div
                ref={isAnchored ? anchored.ref : undefined}
                className="popup__content"
                style={isAnchored ? anchored.style : undefined}
                data-anim={isAnchored ? anchored.anim : comp.anim}
                data-state={state}
                onClick={(event) => event.stopPropagation()}
            >
                <comp.Component onClose={onClose} details={comp.details} />
            </div>
        </div>
    );
}

/**
 * The one place a popup is drawn, in a portal so no screen's overflow can clip it — and the one place
 * the page behind it is frozen, so every popup gets the behaviour without asking for it.
 *
 * `overflow: hidden` alone is not enough: iOS Safari scrolls the body regardless, which is how a
 * person ends up dragging the page around underneath an open dialog. Pinning the body with `position:
 * fixed` at its current offset is the only thing that holds there, and the offset is put back on the
 * way out so closing a popup does not jump to the top of the list you were reading.
 */
export default function PopupPaper() {
    const comps = usePopup((state) => state.comps);
    const isAnyOpen = comps.length > 0;

    useEffect(() => {
        if (!isAnyOpen) return;

        const { body } = document;
        const scrollY = window.scrollY;
        const previous = { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow };

        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.width = "100%";
        body.style.overflow = "hidden";

        return () => {
            Object.assign(body.style, previous);
            window.scrollTo(0, scrollY);
        };
        /**
         * Keyed on "is anything open", never on how many. Opening a second popup over the first would
         * otherwise re-run this, and by then `window.scrollY` reads 0 because the body is already
         * pinned — so closing both would land the person at the top of the page.
         */
    }, [isAnyOpen]);

    if (!comps.length) return null;

    return createPortal(
        <>
            {comps.map((comp, index) => (
                <Popup key={comp.name} comp={comp} index={index} />
            ))}
        </>,
        document.body
    );
}
